import { useQueries } from '@tanstack/react-query';
import { getVehicleAlertsCount } from '../../services/VehiclesService';
import { VehicleAlertsCount } from '../../types/Vehicles';
import useGetFleet from '../useGetFleetDetails';
import globalStore from '../../../store/GlobalStore';
import {
  getEndOfTheDay,
  getEndOfMonth,
  getEndOfWeek,
  getStartOfMonth,
  getStartOfTheDay,
  getStartOfWeek,
} from '../../../utils/dateUtils';

const buildVehicleAlertsCountQuery = (): string => (
  `?dayStartAt=${getStartOfTheDay().toISOString()}`
  + `&dayEndAt=${getEndOfTheDay().toISOString()}`
  + `&weekStartAt=${getStartOfWeek().toISOString()}`
  + `&weekEndAt=${getEndOfWeek().toISOString()}`
  + `&monthStartAt=${getStartOfMonth().toISOString()}`
  + `&monthEndAt=${getEndOfMonth().toISOString()}`
);

export function useVehicleAlertsCounts(
  fleetId: string,
  ids: string[],
): { alertCounts: Map<string, VehicleAlertsCount>; isLoading: boolean } {
  const { data: fleetData } = useGetFleet(fleetId);
  const fleetTimezoneOffset = fleetData?.settings?.timezoneOffset;
  const isTimezoneReady = fleetTimezoneOffset !== undefined;

  const results = useQueries({
    queries: ids.map((id) => ({
      queryKey: ['vehicle-alerts-count', id, fleetId, fleetTimezoneOffset],
      queryFn: () => {
        globalStore.setTimezoneOffset(fleetTimezoneOffset);
        return getVehicleAlertsCount(fleetId, id, buildVehicleAlertsCountQuery());
      },
      retry: 1,
      retryDelay: 3000,
      staleTime: 60_000,
      enabled: !!id && !!fleetId && isTimezoneReady,
    })),
  });

  const isLoading = ids.length > 0 && results.some((val) => val.isLoading || val.isFetching);

  const alertCounts = new Map<string, VehicleAlertsCount>();
  results.forEach((res, i) => {
    if (res.data) {
      alertCounts.set(ids[i], res.data);
    }
  });

  return { alertCounts, isLoading };
}
